import { CommonModule } from '@angular/common';
import {
    Component,
    EventEmitter,
    HostListener,
    Input,
    Output,
    ElementRef,
    ViewChild,
    signal,
    effect,
    OnDestroy,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { CheckboxComponent } from '../checkbox/checkbox.component';
import { BehaviorSubject, fromEvent, Subscription } from 'rxjs';
import { throttleTime } from 'rxjs/operators';
import { DropdownService } from 'src/utils/dropdown.service';

export interface MultiselectOption {
    value: string | number;
    label: string;
    active: boolean;
}

@Component({
    selector: 'multiselect-table',
    imports: [CommonModule, FormsModule, CheckboxComponent],
    templateUrl: './multiselect-table.component.html',
    standalone: true,
})
export class MultiselectTableComponent implements OnDestroy {
    @Input() options: MultiselectOption[] = [];
    @Input() icon: string = 'fi fi-rr-filter';
    @Input() placeholder: string = 'Seleccione las opciones deseadas';
    @Input() closeOnSelect: boolean = false;
    @Output() optionsSelectedChange = new EventEmitter<MultiselectOption[]>();
    @Output() optionSelectedChange = new EventEmitter<MultiselectOption>();

    private searchTermSubject = new BehaviorSubject<string>('');
    private scrollSubscription?: Subscription;
    searchTerm$ = this.searchTermSubject.asObservable();
    isOpen = signal(false);
    dropdownStyles: { [key: string]: string } = {};
    dropdownId: string;

    @ViewChild('selectContainer', { static: false }) selectContainer!: ElementRef<HTMLElement>;
    @ViewChild('dropdownEl', { static: false }) dropdownEl!: ElementRef<HTMLElement>;

    // Nuevas propiedades
    private resizeObserver: ResizeObserver | null = null;
    private observedScrollParent: Element | Window | null = null;
    private appendedToBody = false;

    constructor(private dropdownService: DropdownService) {
        this.dropdownId = `multiselect-${Math.random().toString(36).substr(2, 9)}`;

        effect(() => {
            const openDropdownId = this.dropdownService.getOpenDropdown()();
            this.handleDropdownChange(openDropdownId);
        });
    }


    ngOnDestroy() {
        this.removePositionListeners();
        this.removeDropdownFromBody();
    }

    get searchTerm(): string {
        return this.searchTermSubject.value;
    }

    set searchTerm(value: string) {
        this.searchTermSubject.next(value);
    }

    get filteredOptions() {
        const searchValue = this.searchTermSubject.value;
        return this.options.filter((option) =>
            option.label.toLowerCase().includes(searchValue.toLowerCase())
        );
    }

    toggleDropdown() {
        this.dropdownService.toggleDropdown(this.dropdownId);
        this.handleDropdownChange(this.dropdownService.getOpenDropdown()());
    }

    clearSearch() {
        this.searchTerm = '';
    }

    toggleOption(option: MultiselectOption, event?: Event) {
        if (event) {
            event.stopPropagation();
        }
        option.active = !option.active;
        this.optionSelectedChange.emit(option);
        const selected = this.options.filter((o) => o.active);
        this.optionsSelectedChange.emit(selected);
        
        if (this.closeOnSelect) {
            setTimeout(() => {
                this.dropdownService.closeDropdown();
            }, 150);
        }
    }

    @HostListener('document:click', ['$event'])
    onClickOutside(event: MouseEvent) {
        if (!this.isOpen()) return;

        const target = event.target as HTMLElement;
        const clickedInsideTrigger = this.selectContainer?.nativeElement?.contains(target);
        const clickedInsideDropdown = this.dropdownEl?.nativeElement?.contains(target);

        if (!clickedInsideTrigger && !clickedInsideDropdown) {
            this.optionsSelectedChange.emit(this.options.filter((option) => option.active));
            this.dropdownService.closeDropdown();
            this.handleDropdownChange(null);
        }
    }

    @HostListener('window:resize', ['$event'])
    onWindowResize(event: Event) {
        if (this.isOpen()) {
            this.calculateDropdownPosition();
        }
    }

    private calculateDropdownPosition() {
        if (!this.selectContainer || !this.selectContainer.nativeElement) return;

        const rect = this.selectContainer.nativeElement.getBoundingClientRect();
        const dropdownWidth = 327;
        const margin = 16;
        const windowWidth = window.innerWidth;

        let leftPosition = rect.left;

        if (leftPosition + dropdownWidth > windowWidth - margin) {
            leftPosition = windowWidth - dropdownWidth - margin;
        }
        if (leftPosition < margin) {
            leftPosition = margin;
        }

        const topValue = rect.bottom + 8;

        this.dropdownStyles = {
            top: `${topValue}px`,
            left: `${leftPosition}px`,
            maxHeight: '304px',
            width: `${dropdownWidth}px`,
        };

        if (this.appendedToBody && this.dropdownEl?.nativeElement) {
            const el = this.dropdownEl.nativeElement as HTMLElement;
            el.style.top = this.dropdownStyles['top'];
            el.style.left = this.dropdownStyles['left'];
            el.style.maxHeight = this.dropdownStyles['maxHeight'];
            el.style.width = this.dropdownStyles['width'];
        }
    }

    private getScrollableParent(element: HTMLElement | null): Element | Window {
        if (!element) return window;
        let parent: HTMLElement | null = element.parentElement;
        while (parent && parent !== document.body) {
            const style = getComputedStyle(parent);
            const overflow = `${style.overflow}${style.overflowY}${style.overflowX}`;
            if (/(auto|scroll|overlay)/.test(overflow)) {
                return parent;
            }
            parent = parent.parentElement;
        }
        return window;
    }


    private setupPositionListeners() {
        this.removePositionListeners();

        if (!this.selectContainer || !this.selectContainer.nativeElement) return;

        const scrollParent = this.getScrollableParent(this.selectContainer.nativeElement);
        this.observedScrollParent = scrollParent;

        if (scrollParent === window) {
            this.scrollSubscription = fromEvent(window, 'scroll', { passive: true })
                .pipe(throttleTime(16))
                .subscribe(() => {
                    if (this.isOpen()) this.calculateDropdownPosition();
                });
        } else {
            this.scrollSubscription = fromEvent(scrollParent as Element, 'scroll', { passive: true })
                .pipe(throttleTime(16))
                .subscribe(() => {
                    if (this.isOpen()) this.calculateDropdownPosition();
                });
        }

        this.resizeObserver = new ResizeObserver(() => {
            if (this.isOpen()) {
                this.calculateDropdownPosition();
            }
        });

        try {
            if (scrollParent === window) {
                this.resizeObserver.observe(document.documentElement);
            } else {
                this.resizeObserver.observe(scrollParent as Element);
            }
            this.resizeObserver.observe(this.selectContainer.nativeElement);
        } catch (e) {
            console.warn('ResizeObserver no disponible en este navegador:', e);
        }
    }

    private removePositionListeners() {
        if (this.scrollSubscription) {
            this.scrollSubscription.unsubscribe();
            this.scrollSubscription = undefined;
        }
        if (this.resizeObserver) {
            try {
                this.resizeObserver.disconnect();
            } catch { }
            this.resizeObserver = null;
        }
        this.observedScrollParent = null;
    }

    private appendDropdownToBody() {
        if (!this.dropdownEl || !this.dropdownEl.nativeElement) return;
        if (this.appendedToBody) return;

        try {
            document.body.appendChild(this.dropdownEl.nativeElement);
            this.appendedToBody = true;
            this.calculateDropdownPosition();
            const el = this.dropdownEl.nativeElement as HTMLElement;
            el.style.position = 'fixed';
            el.style.zIndex = '9999';
            el.style.boxShadow = '0px 8px 16px 0px rgba(0,0,0,0.16)';
        } catch (e) {
            console.warn('No se pudo anexar dropdown al body:', e);
        }
    }

    private removeDropdownFromBody() {
        if (!this.dropdownEl || !this.dropdownEl.nativeElement) return;
        if (!this.appendedToBody) return;

        try {
            if (document.body.contains(this.dropdownEl.nativeElement)) {
                document.body.removeChild(this.dropdownEl.nativeElement);
            }
        } catch (e) {
        } finally {
            this.appendedToBody = false;
        }
    }

    private handleDropdownChange(openDropdownId: string | null) {
        const shouldOpen = openDropdownId === this.dropdownId;
        const currentlyOpen = this.isOpen();

        if (shouldOpen === currentlyOpen) {
            return;
        }

        if (shouldOpen) {
            this.isOpen.set(true);
            this.searchTerm = '';
            requestAnimationFrame(() => {
                this.calculateDropdownPosition();
                this.appendDropdownToBody();
                this.setupPositionListeners();
            });
        } else {
            this.isOpen.set(false);
            this.removePositionListeners();
            this.removeDropdownFromBody();
        }
    }
}
